import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo } from 'react';
import cn from 'classnames';
import { compact } from 'lodash-es';
import { Box, DropdownMenu, TableList, TableListActions, Typography } from 'seagull';
import { Copy, EditSquare, Trash } from 'seagull/icons';
import { useLocaleKeys } from '~/i18n';
import { formatDateWithTime } from '~/models/time/selectors';
import { AdditionalRecipientsCell } from './draftCells/AdditionalRecipientsCell/AdditionalRecipientsCell';
import AgreementNameCell from './draftCells/AgreementNameCell';
import AgreementNoteCell from './draftCells/AgreementNoteCell';
import AutoApproveAmendmentsCell from './draftCells/AutoApproveAmendments';
import AutoApproveOutOfScopeChargesCell from './draftCells/AutoApproveOutOfScopeChargesCell';
import { ClientCell } from './draftCells/ClientCell/ClientCell';
import { ContactCell } from './draftCells/ContactCell';
import CreditCardProcessingFeesCell from './draftCells/CreditCardProcessingFeesCell';
import EffectiveDateCell from './draftCells/EffectiveDateCell';
import { ExpirationDateCell } from './draftCells/ExpirationDateCell';
import { IntroductoryMessageCell } from './draftCells/IntroductoryMessageCell';
import { MainSignatoryCell } from './draftCells/MainSignatoryCell/MainSignatoryCell';
import ManualPaymentApprovalCell from './draftCells/ManualPaymentApprovalCell';
import NetTermsCell from './draftCells/NetTermsCell';
import PaymentMethodRequiredCell from './draftCells/PaymentMethodRequiredCell';
import TermsOfServiceCell from './draftCells/TermsOfServiceCell';
import EditDraftServiceTable from './EditDraftServiceTable/EditDraftServiceTable';
import { getModifiedBy, makeEditableTableItem } from './EditDraftsTable.utils';
import styles from './EditDraftsTableRow.module.scss';
import { EditDraftsTableColumns } from './types';
const testIdPrefix = 'edit-drafts-table';
const EditDraftsTableRowComponent = (props)=>{
    const { index, visibleColumns, draft, getContact, getClientName, actions, worksAt, highlighted, onHighlightEnd } = props;
    const { t: draftsTexts } = useLocaleKeys('pages.draftsDashboard');
    const contact = getContact(draft);
    const isDisabledForScheduledDrafts = draft.isScheduled();
    if (worksAt == null) return null;
    const openDraftUpdateModal = (options)=>actions.openDraftUpdateModal(draft, options);
    const EditableCellDefaultProps = {
        draft,
        index,
        onOpenModal: openDraftUpdateModal,
        isDisabled: isDisabledForScheduledDrafts,
        disabledTooltip: isDisabledForScheduledDrafts ? 'In order to edit a scheduled proposal, its scheduling must be canceled.' : undefined
    };
    const renderExpandedContent = ()=>/*#__PURE__*/ _jsx(Box, {
            className: styles.expandedContent,
            testId: "expanded-content-".concat(draft.id()),
            children: /*#__PURE__*/ _jsx(EditDraftServiceTable, {
                draft: draft,
                onOpenModal: openDraftUpdateModal,
                onAddServices: (services, packageId)=>actions.addServices(draft, services, packageId),
                contact: contact,
                worksAt: worksAt
            })
        });
    return /*#__PURE__*/ _jsx(TableList.Row, {
        rowId: draft.id(),
        testId: "".concat(testIdPrefix, "-row-").concat(draft.id()),
        className: cn({
            [styles.highlight]: highlighted
        }),
        onAnimationEnd: ()=>onHighlightEnd(draft.id()),
        items: compact([
            visibleColumns[EditDraftsTableColumns.PrimaryContact] && makeEditableTableItem(/*#__PURE__*/ _jsx(ContactCell, _object_spread_props(_object_spread({}, EditableCellDefaultProps), {
                getContact: getContact
            }))),
            visibleColumns[EditDraftsTableColumns.Client] && makeEditableTableItem(/*#__PURE__*/ _jsx(ClientCell, _object_spread_props(_object_spread({}, EditableCellDefaultProps), {
                getClientName: getClientName,
                testId: "client-".concat(index)
            }))),
            visibleColumns[EditDraftsTableColumns.MainSignatory] && makeEditableTableItem(/*#__PURE__*/ _jsx(MainSignatoryCell, _object_spread_props(_object_spread({}, EditableCellDefaultProps), {
                contact: contact,
                testId: "main-signatory-".concat(index)
            }))),
            visibleColumns[EditDraftsTableColumns.AdditionalRecipients] && makeEditableTableItem(/*#__PURE__*/ _jsx(AdditionalRecipientsCell, _object_spread_props(_object_spread({}, EditableCellDefaultProps), {
                contact: contact
            }))),
            visibleColumns[EditDraftsTableColumns.AgreementName] && makeEditableTableItem(/*#__PURE__*/ _jsx(AgreementNameCell, _object_spread_props(_object_spread({}, EditableCellDefaultProps), {
                contact: contact,
                worksAt: worksAt
            }))),
            visibleColumns[EditDraftsTableColumns.EffectiveDate] && makeEditableTableItem(/*#__PURE__*/ _jsx(EffectiveDateCell, _object_spread({}, EditableCellDefaultProps))),
            visibleColumns[EditDraftsTableColumns.ModifiedBy] && {
                value: /*#__PURE__*/ _jsx(Typography, {
                    testId: "last-modified-by-".concat(index),
                    children: getModifiedBy(draft)
                })
            },
            visibleColumns[EditDraftsTableColumns.LastModified] && {
                value: /*#__PURE__*/ _jsx(Typography, {
                    testId: "last-modified-on-".concat(index),
                    children: formatDateWithTime(draft.lastModifiedOn())
                })
            },
            visibleColumns[EditDraftsTableColumns.ExpirationDate] && makeEditableTableItem(/*#__PURE__*/ _jsx(ExpirationDateCell, _object_spread({}, EditableCellDefaultProps))),
            visibleColumns[EditDraftsTableColumns.IntroductoryMessage] && makeEditableTableItem(/*#__PURE__*/ _jsx(IntroductoryMessageCell, _object_spread_props(_object_spread({}, EditableCellDefaultProps), {
                contact: contact,
                worksAt: worksAt
            }))),
            visibleColumns[EditDraftsTableColumns.TermsOfService] && makeEditableTableItem(/*#__PURE__*/ _jsx(TermsOfServiceCell, _object_spread({}, EditableCellDefaultProps))),
            visibleColumns[EditDraftsTableColumns.AgreementNote] && makeEditableTableItem(/*#__PURE__*/ _jsx(AgreementNoteCell, _object_spread_props(_object_spread({}, EditableCellDefaultProps), {
                contact: contact,
                worksAt: worksAt
            }))),
            visibleColumns[EditDraftsTableColumns.AutoApproveAmendments] && makeEditableTableItem(/*#__PURE__*/ _jsx(AutoApproveAmendmentsCell, _object_spread({}, EditableCellDefaultProps))),
            visibleColumns[EditDraftsTableColumns.AutoApproveOutOfScopeCharges] && makeEditableTableItem(/*#__PURE__*/ _jsx(AutoApproveOutOfScopeChargesCell, _object_spread({}, EditableCellDefaultProps))),
            visibleColumns[EditDraftsTableColumns.NumberOfDaysUntilPaymentIsDue] && makeEditableTableItem(/*#__PURE__*/ _jsx(NetTermsCell, _object_spread({}, EditableCellDefaultProps))),
            visibleColumns[EditDraftsTableColumns.PaymentMethodRequiredForSigning] && makeEditableTableItem(/*#__PURE__*/ _jsx(PaymentMethodRequiredCell, _object_spread({}, EditableCellDefaultProps))),
            visibleColumns[EditDraftsTableColumns.CreditCardProcessingFees] && makeEditableTableItem(/*#__PURE__*/ _jsx(CreditCardProcessingFeesCell, _object_spread_props(_object_spread({}, EditableCellDefaultProps), {
                contact: contact
            }))),
            visibleColumns[EditDraftsTableColumns.ManualApprovalOfEachPayment] && makeEditableTableItem(/*#__PURE__*/ _jsx(ManualPaymentApprovalCell, _object_spread({}, EditableCellDefaultProps)))
        ]),
        actionColumn: /*#__PURE__*/ _jsxs(TableListActions, {
            bi: {
                button: {
                    name: 'edit_draft_row_view'
                },
                menuTrigger: {
                    name: 'edit_draft_row_actions'
                }
            },
            testId: "table-list-actions-".concat(draft.id()),
            children: [
                /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                    bi: {
                        name: 'table_list_actions_edit'
                    },
                    iconStart: /*#__PURE__*/ _jsx(EditSquare, {}),
                    onClick: ()=>actions.editDraft(draft),
                    testId: "table-list-actions-edit-".concat(draft.id()),
                    children: draftsTexts.editDraft()
                }),
                /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                    bi: {
                        name: 'table_list_actions_duplicate'
                    },
                    iconStart: /*#__PURE__*/ _jsx(Copy, {}),
                    onClick: ()=>actions.duplicateDraft(draft.id()),
                    testId: "table-list-actions-duplicate-".concat(draft.id()),
                    children: draftsTexts.duplicateDraft()
                }),
                /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                    bi: {
                        name: 'table_list_actions_discard'
                    },
                    skin: "error",
                    iconStart: /*#__PURE__*/ _jsx(Trash, {}),
                    onClick: ()=>actions.discardDraft(draft),
                    testId: "table-list-actions-discard-".concat(draft.id()),
                    children: draftsTexts.discardDraft()
                })
            ]
        }),
        expandedContent: renderExpandedContent
    });
};
export const EditDraftsTableRow = /*#__PURE__*/ memo(EditDraftsTableRowComponent);
